import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import authService from "../Services/authService";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const prevCountRef = useRef(0);
  const audioRef = useRef(null);
  const isAudioUnlockedRef = useRef(false);

  // ✅ Unlock audio once user interacts with page
  useEffect(() => {
    const unlockAudio = () => {
      isAudioUnlockedRef.current = true;

      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          })
          .catch(() => {
            // Ignored: autoplay block expected
          });
      }

      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };

    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);

    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
  }, []);

  // ✅ This can be called from any component via useNotification()
  const fetchNotificationCount = () => {
    const token = authService.getCurrentUsertoken();
    if (!token) return;

    fetch(authService.getApiUrl() + "notificationcount", {
      method: "POST",
      body: JSON.stringify({
        assign_to: authService.getCurrentuser().id,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        const newCount = result.data || 0;

        if (
          newCount > prevCountRef.current &&
          isAudioUnlockedRef.current &&
          audioRef.current
        ) {
          audioRef.current.play().catch((e) =>
            console.warn("🔇 Audio error:", e.message)
          );
        }

        prevCountRef.current = newCount;
        setNotificationCount(newCount);
      })
      .catch((err) => {
        console.error("🔻 Failed to fetch notification count:", err.message);
      });
  };

  // 🔁 Periodic polling every 60 sec
  useEffect(() => {
    fetchNotificationCount(); // Initial fetch

    const interval = setInterval(() => {
      fetchNotificationCount();
    }, 1 * 60 * 1000); // ⏱ every 5 minutes

    return () => clearInterval(interval);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notificationCount,
        setNotificationCount,
        fetchNotificationCount, // expose this to trigger refresh manually
      }}
    >
      {children}
      {/* 🔊 Hidden audio element */}
      <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
